import React, { useState, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import InputAdornment from "@mui/material/InputAdornment";
import { Spinner } from "reactstrap";
import * as Yup from "yup";
import dayjs from "dayjs";
import { ChangeRequestModule } from "../../../constants/Module";
import { Action } from "../../../constants/Common";
import SearchDropDownListPaginationComponent from "../../casp/common/SearchDropDownPaginationList";
import { getAllDeliverableList } from "../../../api/casp/deliverable";
import IndividualMilestone from "./individualMilestone";
import { DeliverableStatus } from "../../../constants/Status";
import Autocomplete from '@mui/material/Autocomplete';
import { MilestoneBudgetType } from "../../../constants/MilestoneBudget";
import { toast } from "react-toastify";
import _ from "lodash"
import SweetAlert from "react-bootstrap-sweetalert";
import { withTranslation } from "react-i18next";

const AddMilestoneModule = (props) => {
  const newMilestoneObject = {
    name: "",
    description: "",
    cost: "",
    weight: "",
    // aim: "",
    output: "",
    deliverableId: "",
    deliverableObject: undefined,
    estimatedStartDate: "",
    estimatedEndDate: "",
    type: '',
    budgetType: '',
    plannedCost: '',
    items: []
  };
  const [refreshDeliverables, setRefreshDeliverables] = useState(+new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [updatedDeliverable, setUpdatedDeliverable] = useState()
  const [selectedDeliverable, setSelectedDeliverable] = useState()
  const [milestoneCreationLoading, setMilestoneCreationLoading] = useState(false);
  const [trigger, setTrigger] = useState(new Date())
  const [isDisable, setIsDisable] = useState(true)
  const [openAlert, setOpenAlert] = useState(false)

  const milestoneCreateValidation = Yup.object().shape(
    {
      name: Yup.string()
        .strict(true)
        .trim(props.t('Must not include leading and trailing spaces'))
        .min(2, props.t("Too Short!"))
        .max(250, props.t("Too Long!"))
        .matches(/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/? ]*$/, props.t("Name should be in proper type"))
        .required(props.t("Please enter name")),
      description: Yup.string()
        .strict(true)
        .trim(props.t('Must not include leading and trailing spaces'))
        .min(2, props.t("Too Short!"))
        .max(2000, props.t("Too Long!"))
        .required(props.t("Please Enter description")),
      // aim: Yup.string()
      //   .strict(true)
      //   .trim('Must not include leading and trailing spaces')
      //   .min(2, "Too Short!")
      //   .max(250, "Too Long!")
      //   .required("Please Enter aim"),
      output: Yup.string()
        .strict(true)
        .trim(props.t('Must not include leading and trailing spaces'))
        .min(2, props.t("Too Short!"))
        .max(2000, props.t("Too Long!"))
        .required(props.t("Please Enter output")),
      deliverableId: Yup.number()
        .integer()
        .required(props.t("Please select a deliverable")),
      deliverableObject: Yup.object().required(props.t("Please select a deliverable")),
      cost: Yup.number()
        .moreThan(0, props.t("Cost cannot be 0 or negative"))
        .required(props.t("Please Enter cost"))
        .test(props.t('two decimal places'), props.t('Numbers can only have two decimal places'), (value) => {
          return value === null || (value && /^\d+(\.\d{0,2})?$/.test(String(value)));
        }),
      weight: Yup.number()
        .min(0, props.t('Too short!'))
        .integer()
        .required(props.t("Please Enter weight"))
        .max(100, props.t("Weight cannot exceed more than 100")),
      estimatedStartDate: Yup.date()
        .typeError(props.t("Given date is invalid type"))
        .required(props.t("Please Enter start date"))
        .when(['deliverableId','estimatedStartDate'], {
          is: (deliverableId, estimatedStartDate) => deliverableId && estimatedStartDate,
          then: (schema) => schema.min(updatedDeliverable?.startDate?.new ?? selectedDeliverable?.startDate, `${props.t('Start date cannot be less than deliverable start date')} ' ' ${dayjs(updatedDeliverable?.startDate?.new ?? selectedDeliverable?.startDate).format('MM/DD/YYYY')}`).max(updatedDeliverable?.endDate?.new ?? selectedDeliverable?.endDate, `${props.t('Start date cannot be greater than deliverable end date')} ' ' ${dayjs(updatedDeliverable?.endDate?.new ?? selectedDeliverable?.endDate).format('MM/DD/YYYY')}`)
        })
        .when("estimatedEndDate", {
          is: (estimatedEndDate) => {
            return !!estimatedEndDate;
          },
          then: (schema) =>
            schema.max(
              Yup.ref("estimatedEndDate"),
              props.t("Start date cannot be after End date")
            ),
        }),
      estimatedEndDate: Yup.date()
        .typeError(props.t("Given date is invalid type"))
        .required(props.t("Please Enter end date"))
        .when(['deliverableId', 'estimatedEndDate'], {
          is: (deliverableId, estimatedEndDate) => deliverableId && estimatedEndDate,
          then: (schema) => schema.max(updatedDeliverable?.endDate?.new ?? selectedDeliverable?.endDate, `${props.t('End date cannot be greater than deliverable end date')} ' ' ${dayjs(updatedDeliverable?.endDate?.new ?? selectedDeliverable?.endDate).format('MM/DD/YYYY')}`)
        })
        .when("estimatedStartDate", {
          is: (estimatedStartDate) => !!estimatedStartDate,
          then: (schema) =>
            schema.min(
              Yup.ref("estimatedStartDate"),
              props.t("End date cannot be before Start date")
            ),
        }),
        type: Yup.string()
            .when('items', {
                is: items => items && items.length > 0,
                then: Yup.string()
                .strict(true)
                .trim(props.t('Must not include leading and trailing spaces'))
                .max(250, props.t("Too Long!"))
                .optional(),
                otherwise: Yup.string()
                .strict(true)
                .trim(props.t('Must not include leading and trailing spaces'))
                .max(250, props.t("Too Long!"))
                .required(props.t('Budget type is required'))
            }),
        budgetType: Yup.string()
            .when('type', {
                is: 'Others',
                then: Yup.string()
                .strict(true)
                .trim(props.t('Must not include leading and trailing spaces'))
                .max(250, props.t("Too Long!"))
                .required(props.t('Please enter custom budget type')),
                otherwise: Yup.string()
            }),
        plannedCost: Yup.number()
            .when('items', {
            is: items => items && items.length > 0,
            then: Yup.number().optional()
                .typeError(props.t("Planned cost must be a number"))
                .moreThan(0, props.t("Planned cost cannot be 0 or negative"))
                .when('deliverableId', {
                    is: (deliverableId) => deliverableId,
                    then: (schema) => schema.max(updatedDeliverable?.cost?.new ?? selectedDeliverable.cost , `${props.t('Cost of milestone can not exceed the deliverable cost')} ' ' ${updatedDeliverable?.cost?.new ?? selectedDeliverable?.cost}`),
                }),
            otherwise: Yup.number()
                .required('Planned cost is required')
                .moreThan(0, "Planned cost cannot be 0 or negative")
                .typeError("Planned cost must be a number")
                .when('deliverableId', {
                    is: (deliverableId) => deliverableId,
                    then: (schema) => schema.max(updatedDeliverable?.cost?.new ?? selectedDeliverable.cost, `${props.t('Cost of milestone can not exceed the deliverable cost')} ' ' ${updatedDeliverable?.cost?.new ?? selectedDeliverable?.cost}`),
                }),           
             }),
        items: Yup.array().of(Yup.object().shape({
            budgetType: Yup.string()
                .strict(true)
                .trim(props.t('Must not include leading and trailing spaces'))
                .max(250, props.t("Too Long!"))
                .required(),
            plannedCost: Yup.number().required()
                .moreThan(0, props.t("Planned cost cannot be 0 or negative"))
            })
        ).required().min(1).max(10)
    },
    [
      ['estimatedStartDate', 'estimatedEndDate'], 
      ['estimatedStartDate','estimatedStartDate'],
      ['estimatedEndDate', 'estimatedEndDate'], 
      ['estimatedStartDate','deliverableId']
  ]);

  const createMilestoneFormik = useFormik({
    initialValues: {
      ...newMilestoneObject,
    },
    validationSchema: milestoneCreateValidation,
    validateOnMount: true,
    onSubmit: (values) => {
      try {
        let payload = {
          ...values,
          deliverableCost: updatedDeliverable?.cost?.new ?? selectedDeliverable?.cost
        }
        payload = _.omit(payload, 'budgetType')
        payload = _.omit(payload, 'plannedCost')
        payload = _.omit(payload, 'type')
        setIsLoading(true);
        props.validate(
          {
            module: ChangeRequestModule.MILESTONE,
            event: Action.CREATE,
          },
          payload,
          () => {
            createMilestoneFormik.resetForm({
              ...newMilestoneObject,
            });
            setRefreshDeliverables(+new Date());
            setIsLoading(false);
          },
          () => {
            setIsLoading(false);
          }
        );
      } catch (e) {
        // not handle any changes
      }
    },
  });

  const handleKeyDownWeight = (event) => {

    if (event.keyCode === 8 || event.keyCode === 13) {
      return;
    }
   
    const regex = /^[0-9]+$/;

    if (!regex.test(event.key)) {
      event.preventDefault(); 
    }
  }

  const handleKeyDownCost = (event) => {

    if (event.keyCode === 8 || event.keyCode === 13) {
        return;
    }
    const value = event.target.value;
    const regex = /^\d*\.?\d{0,2}$/;
    
    if (!regex.test(value + event.key)) {
        event.preventDefault(); 
    }
}

  const handleTypeChange = (_, value) => {
    if (value !== 'Others') {
      createMilestoneFormik.setFieldValue("budgetType", value ? value : "");
    } else {
        createMilestoneFormik.setFieldValue("budgetType", "");
    }

    createMilestoneFormik.setFieldValue("type", value ? value : '');
  }

  const calculateBudgetItemCost = () => {
      if (createMilestoneFormik.values?.items.length === 1) {
          return parseFloat(createMilestoneFormik.values?.items[0]?.plannedCost)
      } else if (createMilestoneFormik.values?.items.length > 1) {
          return createMilestoneFormik.values.items.reduce((prev, current) => {
              return (prev.plannedCost ? parseFloat(prev.plannedCost) : prev) + parseFloat(current.plannedCost)
          })
      }
      
      return 0
  }

  const handleAddRow = () => {
      if (createMilestoneFormik.values.items.length >= 10) {
        return toast.error("You cannot add more than 10 item.");
      }
      
      if (createMilestoneFormik.values.items.length > 0) {
          let existingType = createMilestoneFormik.values.items.find((data) => data.budgetType.toLowerCase() === createMilestoneFormik.values.budgetType.toLowerCase())
          if (existingType) {
              return toast.error("Budget type should be unique");
          }
      }
      createMilestoneFormik.setFieldTouched("type", false, false);
      createMilestoneFormik.setFieldTouched("budgetType", false, false);
      createMilestoneFormik.setFieldTouched("plannedCost", false, false);
      createMilestoneFormik.setValues({
      ...createMilestoneFormik.values,
      cost : calculateBudgetItemCost() + parseFloat(createMilestoneFormik.values?.plannedCost),
      items: [...createMilestoneFormik.values.items, {
          type: createMilestoneFormik.values.type,
          budgetType: createMilestoneFormik.values.type === "Others" ? createMilestoneFormik.values.budgetType : createMilestoneFormik.values.type,
          plannedCost: createMilestoneFormik.values.plannedCost
      }]
      });
      createMilestoneFormik.setFieldValue("type", "")
      createMilestoneFormik.setFieldValue("budgetType", "")
      createMilestoneFormik.setFieldValue("plannedCost", "")
      setTrigger(+new Date())
  };

  const handleDeleteRow = (indexToDelete) => {
      const updatedRows = createMilestoneFormik.values.items.filter((_, index) => index !== indexToDelete);
      createMilestoneFormik.setFieldValue("items", updatedRows)
      let deletedBudget = createMilestoneFormik.values.items.find((_, index) => index === indexToDelete);
      createMilestoneFormik.setFieldValue("cost", calculateBudgetItemCost() - deletedBudget.plannedCost)
  };

  useEffect(() => {
      if (createMilestoneFormik.errors.plannedCost || createMilestoneFormik.errors.budgetType || (createMilestoneFormik.values.type === 'Others' && !createMilestoneFormik.values.budgetType)) {
        setIsDisable(true)
      } else if ((!createMilestoneFormik.errors.plannedCost && createMilestoneFormik.values.plannedCost) && (!createMilestoneFormik.errors.budgetType && createMilestoneFormik.values.budgetType)) {
        setIsDisable(false)
      } else {
          setIsDisable(true)
      }
  }, [createMilestoneFormik.errors.plannedCost, createMilestoneFormik.errors.budgetType, createMilestoneFormik.values.plannedCost, createMilestoneFormik.values.budgetType, createMilestoneFormik.values.type])

  return (
    <div
      className="editDeliverable_height"
      style={{ height: "auto", maxHeight: "1200px" }}
    >
      <div
        className="dashed_border position-relative mt-3 mb-5"
        style={{ paddingBottom: "10px" }}
      >
        <div className="row">
          <div className="col-md-12 mt-2">
            <TextField
              fullWidth
              id="outlined-multiline-static"
              label={props.t("Name")}
              name="name"
              disabled={isLoading}
              value={createMilestoneFormik.values.name}
              onChange={createMilestoneFormik.handleChange}
              onBlur={createMilestoneFormik.handleBlur}
              required
            />
            {createMilestoneFormik.touched.name &&
              createMilestoneFormik.errors.name && (
                <span className="text-danger d-block">
                  {createMilestoneFormik.errors.name}
                </span>
              )}
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 mt-2">
            <TextField
              fullWidth
              id="outlined-multiline-static"
              label={props.t("Description")}
              name="description"
              type="text"
              disabled={isLoading}
              multiline
              value={createMilestoneFormik.values.description}
              onChange={createMilestoneFormik.handleChange}
              onBlur={createMilestoneFormik.handleBlur}
              required
            />
            {createMilestoneFormik.touched.description &&
              createMilestoneFormik.errors.description && (
                <span className="text-danger d-block">
                  {createMilestoneFormik.errors.description}
                </span>
              )}
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 mt-2">
            <FormControl className="agenda_filter w-100">
              <SearchDropDownListPaginationComponent
                label={props.t("Select Deliverable")}
                searchEnabled={false}
                key={refreshDeliverables}
                listCall={getAllDeliverableList}
                onBlur={() => {
                  if (!createMilestoneFormik.touched?.deliverableId) {
                    createMilestoneFormik.setFieldTouched(
                      "deliverableId",
                      true
                    );
                  }
                }}
                hasChangeDeliverable={createMilestoneFormik.values.estimatedStartDate || createMilestoneFormik.values.estimatedEndDate 
                  || createMilestoneFormik.values.plannedCost || createMilestoneFormik.values.type || createMilestoneFormik.values.budgetType 
                  || createMilestoneFormik.values.items.length}
                emitItem={(newValue) => {
                  createMilestoneFormik.setValues({
                    ...createMilestoneFormik.values,
                    deliverableId: newValue ? newValue.id : "",
                    deliverableObject: newValue
                }, true)
                if(newValue) {
                    setSelectedDeliverable({
                        id:newValue?.id,
                        cost: newValue.cost,
                        startDate:dayjs(newValue?.startDate).format("YYYY-MM-DD"),
                        endDate:dayjs(newValue?.endDate).format("YYYY-MM-DD"),
                    })
                    createMilestoneFormik.setFieldTouched("deliverableId", false )  
                } else { 
                    setSelectedDeliverable()
                    createMilestoneFormik.setFieldTouched("deliverableId", true )  
                }
                setUpdatedDeliverable(props?.updatedDeliverable.find((item) => item?.id === newValue?.id))
                createMilestoneFormik.setFieldValue("estimatedStartDate", '')
                createMilestoneFormik.setFieldValue("estimatedEndDate", '')
                createMilestoneFormik.setFieldValue("type", "")
                createMilestoneFormik.setFieldValue("budgetType", "")
                createMilestoneFormik.setFieldValue("plannedCost", "")
                createMilestoneFormik.setFieldValue("items", [])
                createMilestoneFormik.setFieldTouched("estimatedStartDate", false)
                createMilestoneFormik.setFieldTouched("estimatedEndDate", false)
                createMilestoneFormik.setFieldTouched("deliverableId", false)                                        

                }}
                apiParams={{
                  projectId: props?.project?.id,
                  filterFields: ["currentStatus"],
                  filterInputs: [
                    `${DeliverableStatus.ONGOING},${DeliverableStatus.CONFIRMATION_REJECTED}`,
                  ],
                }}
              />
              {createMilestoneFormik.touched.deliverableId &&
                createMilestoneFormik.errors.deliverableId && (
                  <span className="text-danger">
                    {createMilestoneFormik.errors.deliverableId}
                  </span>
                )}
            </FormControl>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mt-2 pe-0">
            <TextField
              onWheel={(e) => e.target.blur()}
              fullWidth
              id="outlined-multiline-static"
              label={props.t("Weight")}
              name="weight"
              type="number"
              disabled={isLoading}
              value={createMilestoneFormik.values.weight}
              onKeyDown={handleKeyDownWeight}
              onChange={createMilestoneFormik.handleChange}
              onBlur={createMilestoneFormik.handleBlur}
              required
            />
            {createMilestoneFormik.touched.weight &&
              createMilestoneFormik.errors.weight && (
                <span className="text-danger d-block">
                  {createMilestoneFormik.errors.weight}
                </span>
              )}
          </div>
          {/* <div className="col-md-6 mt-2">
            <TextField
              fullWidth
              id="outlined-multiline-static"
              label={props.t("Goal")}
              name="aim"
              type="text"
              disabled={isLoading}
              value={createMilestoneFormik.values.aim}
              onChange={createMilestoneFormik.handleChange}
              onBlur={createMilestoneFormik.handleBlur}
              required
            />
            {createMilestoneFormik.touched.aim &&
              createMilestoneFormik.errors.aim && (
                <span className="text-danger d-block">
                  {createMilestoneFormik.errors.aim}
                </span>
              )}
          </div> */}
        </div>
        <div className="row">
          <div className="col-md-12 mt-2">
            <TextField
              fullWidth
              id="outlined-multiline-static"
              label={props.t("Output")}
              name="output"
              type="text"
              multiline
              disabled={isLoading}
              value={createMilestoneFormik.values.output}
              onChange={createMilestoneFormik.handleChange}
              onBlur={createMilestoneFormik.handleBlur}
              required
            />
            {createMilestoneFormik.touched.output &&
              createMilestoneFormik.errors.output && (
                <span className="text-danger d-block">
                  {createMilestoneFormik.errors.output}
                </span>
              )}
          </div>
        </div>
        <div className="row mb-2">
          <div className=" col-md-6 changeRequest_datePicker mt-2 pe-0">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label={props.t("Start Date") + ' *'}
                  format="MM/DD/YYYY"
                  name="estimatedStartDate"
                  disabled={isLoading}
                  value={
                    createMilestoneFormik?.values?.estimatedStartDate
                      ?.toString()
                      .trim()
                      ? createMilestoneFormik.values.estimatedStartDate
                      : null
                  }
                  on
                  slotProps={{
                    field: {
                      clearable: true,
                      onBlur: (_) => {
                        if (
                          !createMilestoneFormik.touched?.estimatedStartDate
                        ) {
                          createMilestoneFormik.setFieldTouched(
                            "estimatedStartDate",
                            true,
                            false
                          );
                        }
                      },
                    },
                    onClear: () => {
                      createMilestoneFormik.setFieldValue(
                        "estimatedStartDate",
                        ""
                      );
                    },
                  }}
                  onChange={(e) => {
                    createMilestoneFormik.setFieldValue(
                      "estimatedStartDate",
                      e ? dayjs(e).format("YYYY-MM-DD") : ""
                    );
                    if (!createMilestoneFormik.touched?.estimatedStartDate) {
                      createMilestoneFormik.setFieldTouched(
                        "estimatedStartDate",
                        true,
                        false
                      );
                    }
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
            {createMilestoneFormik.touched.estimatedStartDate &&
              createMilestoneFormik.errors.estimatedStartDate && (
                <span className="text-danger d-block">
                  {createMilestoneFormik.errors.estimatedStartDate}
                </span>
              )}
          </div>
          <div className="col-md-6 changeRequest_datePicker mt-2">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label={props.t("End Date") + ' *'}
                  format="MM/DD/YYYY"
                  name="estimatedEndDate"
                  disabled={isLoading}
                  value={
                    createMilestoneFormik?.values?.estimatedEndDate
                      ?.toString()
                      .trim()
                      ? createMilestoneFormik.values.estimatedEndDate
                      : null
                  }
                  slotProps={{
                    field: {
                      clearable: true,
                      onBlur: (_) => {
                        if (!createMilestoneFormik.touched?.estimatedEndDate) {
                          createMilestoneFormik.setFieldTouched(
                            "estimatedEndDate",
                            true,
                            false
                          );
                        }
                      },
                    },
                    onClear: () => {
                      createMilestoneFormik.setFieldValue(
                        "estimatedEndDate",
                        ""
                      );
                    },
                  }}
                  onChange={(e) => {
                    createMilestoneFormik.setFieldValue(
                      "estimatedEndDate",
                      e ? dayjs(e).format("YYYY-MM-DD") : ""
                    );
                    if (!createMilestoneFormik.touched?.estimatedEndDate) {
                      createMilestoneFormik.setFieldTouched(
                        "estimatedEndDate",
                        true,
                        false
                      );
                    }
                  }}
                  required
                />
              </DemoContainer>
            </LocalizationProvider>
            {createMilestoneFormik.touched.estimatedEndDate &&
              createMilestoneFormik.errors.estimatedEndDate && (
                <span className="text-danger d-block">
                  {createMilestoneFormik.errors.estimatedEndDate}
                </span>
              )}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-12">
              <p className="title">{props.t('Cost')}: &nbsp; SAR {createMilestoneFormik.values.cost ? parseFloat(createMilestoneFormik.values.cost).toFixed(2) : '0.00'}</p>
          </div>
        </div>
        {createMilestoneFormik.values?.deliverableId && 
          <>
          <div className="both_textfields mb-2 flex-two-col d-flex justify-content-between align-items-start">
          <div className="budgetTextfield mt-2 mx-2 plCurrency currency_small_font">
              {/* <TextField
                  onWheel={(e) => e.target.blur()}
                  label={props.t("Planned Cost")}
                  variant="outlined"
                  size="small"
                  type="number"
                  name="plannedCost"
                  fullWidth
                  value={createMilestoneFormik.values.plannedCost}
                  onKeyDown={handleKeyDownCost}
                  onChange={createMilestoneFormik.handleChange}
                  onBlur={createMilestoneFormik.handleBlur}
                  InputProps={{
                  startAdornment: <InputAdornment position="start">SAR</InputAdornment>,
                  }}
                  disabled={milestoneCreationLoading}
              /> */}
              <TextField
                minimumValue="0"
                sx={{ marginLeft: 3 }}
                id="milestone-cost"
                variant="outlined"
                // label='Deliverable Cost'
                size="small"
                decimalPlaces={2}
                value={createMilestoneFormik.values.plannedCost}
                name = "plannedCost"
                currencySymbol={props.t("SAR")}
                fullWidth
                placeholder={props.t("Planned cost of budget")}
                required
                disabled={milestoneCreationLoading}
                onChange={(e, newValue) => {
                  createMilestoneFormik.setFieldValue('plannedCost', newValue === 0 ? '' : newValue, true)
                }}
                onBlur={() => {
                  createMilestoneFormik.setFieldTouched('plannedCost', true, true)
                }}
              />
              {createMilestoneFormik.touched.plannedCost && createMilestoneFormik.errors.plannedCost && (
                  <span className="text-danger text-wrap">
                  {createMilestoneFormik.errors.plannedCost}
                  </span>
              )}
              </div>
              {createMilestoneFormik.values?.type === 'Others' &&
              <div className="budgetTextfield mt-2">
                  <TextField
                  label={props.t("Custom Budget Type") + ' *'}
                  variant="outlined"
                  size="small"
                  name="budgetType"
                  fullWidth
                  value={createMilestoneFormik.values.budgetType}
                  onChange={createMilestoneFormik.handleChange}
                  onBlur={createMilestoneFormik.handleBlur}
                  disabled={milestoneCreationLoading}
                  />
                  {createMilestoneFormik.touched.budgetType && createMilestoneFormik.errors.budgetType && (
                  <span className="text-danger">
                      {createMilestoneFormik.errors.budgetType}
                  </span>
                  )}
              </div>
              }
              <FormControl className="budgetTextfield mt-2 mx-2">
              <Autocomplete
                  key={trigger}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={createMilestoneFormik.values?.type === '' ? null : createMilestoneFormik.values?.type}
                  size="small"
                  options={MilestoneBudgetType}
                  onChange={handleTypeChange}
                  renderInput={(params) => <TextField {...params}
                  label={props.t("Type") + ' *'}
              />}
              />
              {createMilestoneFormik.touched.type && createMilestoneFormik.errors.type && (
                  <span className="text-danger d-block">{createMilestoneFormik.errors.type}</span>
              )}
              </FormControl>
              <i
              className={`las la-plus-circle d-inline-block mt-3 fw-semibold mt-3 fs-22 text-primary ${isDisable ? "text-secondary" : ""
                  }`}
              style={{ cursor: isDisable ? 'not-allowed' : 'pointer' }}
              onClick={(e) => { isDisable ? e.preventDefault() : handleAddRow() }}
              ></i>
          </div>
          {(!createMilestoneFormik.errors.plannedCost && !createMilestoneFormik.errors.type && !createMilestoneFormik.errors.budgetType && (createMilestoneFormik.values.plannedCost || createMilestoneFormik.values.type)) && (
              <span className="text-danger d-block">Please click the plus (+) button after entering the type and planned cost</span>
          )}
          <div>
              {createMilestoneFormik.values.items?.length > 0 &&
              createMilestoneFormik.values.items.map((_row, index) => (
                  <div key={index}>
                  <div className="both_textfields mt-2 flex-two-col d-flex justify-content-between align-items-start">
                      <div className="budgetTextfield mt-2">
                      {/* <TextField
                          sx={{ marginLeft: 3 }}
                          variant="outlined"
                          size="small"
                          placeholder={props.t("Planned cost of budget")}
                          name={`formik.values.items[${index}].plannedCost`}
                          InputProps={{
                          startAdornment: <InputAdornment position="start">SAR</InputAdornment>,
                          }}
                          fullWidth
                          value={_row.plannedCost}
                          inputProps={{
                              readOnly: true
                          }}
                          className="m-0"
                      /> */}
                      <TextField
                        minimumValue="0"
                        sx={{ marginLeft: 3 }}
                        id="milestone-cost"
                        variant="outlined"
                        // label='Deliverable Cost'
                        size="small"
                        name={`formik.values.items[${index}].plannedCost`}
                        decimalPlaces={2}
                        value={_row.plannedCost}
                        inputProps={{
                          readOnly: true
                        }}
                        currencySymbol={props.t("SAR")}
                        fullWidth
                        placeholder={props.t("Planned cost of budget")}
                        className="m-0"
                        required
                      />
                      </div>
                      <div className="budgetTextfield mt-2">
                      <TextField
                          variant="outlined"
                          size="small"
                          placeholder={props.t("Enter the budget type")}
                          name={`formik.values.items[${index}].budgetType`}
                          fullWidth
                          value={_row.budgetType}
                          inputProps={{
                              readOnly: true
                          }}
                      />
                      </div>
                      <i
                      className="las la-trash-alt fs-22 text-danger mt-3"
                      onClick={() => handleDeleteRow(index)}
                      style={{ cursor: "pointer" }}
                      ></i>
                  </div>
                  </div>
              )
              )}
          </div>
          </>
        }
        <div className="d-flex justify-content-center align-items-center mt-4">
          <button
            className={`add_anotherButton d-flex align-items-center ${
              isLoading || !createMilestoneFormik.isValid ? "bg-secondary" : ""
            }`}
            type="button"
            onClick={createMilestoneFormik.handleSubmit}
            disabled={isLoading || !createMilestoneFormik.isValid}
          >
            {isLoading ? (
              <Spinner className="ml-1" type="border" color="light" size="sm" />
            ) : (
              <i className="ri-add-line ml-1" />
            )}
            <span className="pe-2 ps-1">{props.t('Add')}</span>
          </button>
        </div>
      </div>
      {props?.milestoneList &&
        props?.milestoneList?.map((mL, index) => {
          return (
            <IndividualMilestone
              key={index}
              action={Action.CREATE}
              eachMilestone={mL}
              eachMilestoneIndex={index}
              removeItemFromPayload={props?.removeItemFromPayload}
            />
          );
        })}

    </div>
  );
};

export default withTranslation() (AddMilestoneModule);
